// The route tree, built in code (no generated files):
//
//   rootRoute                      providers, toasts
//   ├── /login                     Microsoft sign-in page (public)
//   └── appRoute (layout, guard)   signed-in shell: sidebar, top bar, global search
//       ├── module routes          from src/modules/*/index.ts (/, /me, /settings/..., anything a lane adds)
//       ├── /$plural               generic list page (or the module's ListPage override)
//       └── /$plural/$id           generic detail page (or the module's DetailPage override)
//
// Static module paths always win over the generic /$plural catch-all (TanStack ranks static segments first).
// Pages are lazy-loaded so each route ships its own chunk.
import type { QueryClient } from '@tanstack/react-query'
import {
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useRouter,
} from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import { AppLayout } from './app/AppLayout'
import { appRoute, rootRoute } from './app/route-base'
import { ClearpolLogo } from './components/Brand'
import { ErrorState, FullPageSpinner, PageSkeleton } from './components/states'
import { Button } from './components/ui/button'
import { Toaster } from './components/ui/toaster'
import { TooltipProvider } from './components/ui/tooltip'
import { errorMessage, isApiError } from './lib/api'
import { safeReturnTo } from './lib/auth'
import { meQueryOptions } from './lib/auth-context'
import { parseSearch, stringifySearch } from './lib/search-params'
import { webModules } from './modules'

function RootLayout() {
  return (
    <TooltipProvider delayDuration={300}>
      <Outlet />
      <Toaster />
    </TooltipProvider>
  )
}

/** Shown when the session check itself fails for a reason other than "not signed in" (server down, network). */
function ShellError({ error }: { error: unknown }) {
  const router = useRouter()
  return (
    <div className="grid min-h-full place-items-center bg-canvas p-6">
      <div className="w-full max-w-md rounded-2xl border border-line bg-white p-8 text-center shadow-sm">
        <ClearpolLogo className="mx-auto h-8" />
        <h1 className="mt-6 text-lg font-semibold text-ink">Clearpol Compliance could not start</h1>
        <p className="mt-2 text-sm text-ink-muted">{errorMessage(error)}</p>
        <Button className="mt-6" onClick={() => void router.invalidate()}>
          <RefreshCw aria-hidden />
          Try again
        </Button>
      </div>
    </div>
  )
}

/** Error inside a page: keep the shell, show the error and a retry. */
function PageError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter()
  return (
    <ErrorState
      error={error}
      onRetry={() => {
        reset()
        void router.invalidate()
      }}
    />
  )
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // A signed-in person who opens /login (bookmark, back button) goes straight to where they were heading.
  beforeLoad: async ({ context, search }) => {
    const s = search as Record<string, unknown>
    if (s.error) return
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null)
    if (me) throw redirect({ href: safeReturnTo(typeof s.returnTo === 'string' ? s.returnTo : null) })
  },
  component: lazyRouteComponent(() => import('./app/LoginPage'), 'LoginPage'),
})

rootRoute.update({ component: RootLayout })

appRoute.update({
  component: AppLayout,
  pendingComponent: () => <FullPageSpinner />,
  errorComponent: ({ error }) =>
    isApiError(error) && error.status === 401 ? <FullPageSpinner /> : <ShellError error={error} />,
  // Unknown deep paths render "Page not found" inside the signed-in shell.
  notFoundComponent: lazyRouteComponent(() => import('./pages/EntityRoutes'), 'NotFoundPage'),
})

export const entityListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/$plural',
  component: lazyRouteComponent(() => import('./pages/EntityRoutes'), 'EntityListRoute'),
  errorComponent: PageError,
})

export const entityDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/$plural/$id',
  component: lazyRouteComponent(() => import('./pages/EntityRoutes'), 'EntityDetailRoute'),
  errorComponent: PageError,
})

export function buildRouteTree() {
  return rootRoute.addChildren([
    loginRoute,
    appRoute.addChildren([...webModules.routes, entityListRoute, entityDetailRoute]),
  ])
}

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree: buildRouteTree(),
    context: { queryClient },
    parseSearch,
    stringifySearch,
    defaultPreload: 'intent',
    // React Query owns caching; the router must not serve stale loader data on top of it.
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: PageSkeleton,
    defaultPendingMs: 200,
    defaultErrorComponent: PageError,
  })
}
